import { Link, useRouterState, useSearch } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ROLE_LABELS, WEBHOOK_EVENTS, type Role, type WebhookEvent } from '@salesbee/contracts';
import { Alert, Badge, Button, Card, CardContent, Checkbox, Field, Input, PageHeader, Select, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, cn, formatDate, formatMoney, segmentedItemClass, segmentedListClass, useToast } from '@salesbee/ui';
import { FeatureGate, Meter, QueryView, Section, useCurrent } from '../components/common';
import { errorMessage, trpc } from '../lib/trpc';

function SettingsTabs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tabs = [
    ['/settings', 'General', (p: string) => p === '/settings'],
    ['/settings/team', 'Team', (p: string) => p.startsWith('/settings/team')],
    ['/settings/billing', 'Billing', (p: string) => p.startsWith('/settings/billing')],
    ['/settings/integrations', 'Integrations & API', (p: string) => p.startsWith('/settings/integrations')],
  ] as const;
  return (
    <div className={cn(segmentedListClass, 'mb-6')}>
      {tabs.map(([to, label, match]) => (
        <Link key={to} to={to} className={segmentedItemClass(match(pathname))}>
          {label}
        </Link>
      ))}
    </div>
  );
}

export function GeneralSettingsPage() {
  const current = useCurrent();
  const settings = trpc.workspace.settings.get.useQuery();
  const update = trpc.workspace.settings.update.useMutation();
  const rename = trpc.workspace.update.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const [name, setName] = useState('');
  const [tz, setTz] = useState('');
  const [industry, setIndustry] = useState('');
  const [icpIndustries, setIcpIndustries] = useState('');
  const [icpRegions, setIcpRegions] = useState('');
  useEffect(() => {
    if (current.data) setName(current.data.name);
    if (settings.data) {
      setTz(settings.data.timezone);
      setIndustry(settings.data.industry ?? '');
      setIcpIndustries(settings.data.icp.industries.join(', '));
      setIcpRegions(settings.data.icp.regions.join(', '));
    }
  }, [current.data, settings.data]);
  const split = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
  return (
    <div>
      <PageHeader title="Settings" />
      <SettingsTabs />
      <Card>
        <CardContent className="grid gap-4 py-6 sm:grid-cols-2">
          <Field label="Workspace name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Time zone"><Input value={tz} onChange={(e) => setTz(e.target.value)} /></Field>
          <Field label="Industry"><Input value={industry} onChange={(e) => setIndustry(e.target.value)} /></Field>
          <Field label="Ideal customer: industries"><Input value={icpIndustries} onChange={(e) => setIcpIndustries(e.target.value)} /></Field>
          <Field label="Ideal customer: provinces/states"><Input value={icpRegions} onChange={(e) => setIcpRegions(e.target.value)} placeholder="ON, BC" /></Field>
          <div className="sm:col-span-2">
            <Button
              loading={update.isPending || rename.isPending}
              onClick={async () => {
                try {
                  if (name && name !== current.data?.name) await rename.mutateAsync({ name });
                  await update.mutateAsync({ timezone: tz, industry, icp: { industries: split(icpIndustries), regions: split(icpRegions).map((r) => r.toUpperCase()) } });
                  success('Settings saved');
                  await utils.invalidate();
                } catch (e) {
                  error('Could not save', errorMessage(e));
                }
              }}
            >
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
      <p className="mt-4 text-sm text-muted-foreground">Caller ID, disclosure and calling hours are on the <Link to="/compliance" className="underline">Compliance</Link> page.</p>
      <div className="mt-8">
        <IndustryTemplates />
      </div>
    </div>
  );
}

const CHANNEL_LABELS: Record<string, string> = { email: 'Email', sms: 'Text', task: 'Call task', call: 'AI call' };

/** Add an industry's pipeline and starter sequences. Additive: existing pipelines, sequences and deals are never changed. */
function IndustryTemplates() {
  const list = trpc.templates.list.useQuery();
  const apply = trpc.templates.applyTemplate.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  return (
    <Section
      title="Industry templates"
      description="Add a pipeline and draft starter sequences for an industry. Nothing you already have is changed, and everything added can be edited or removed. Sequences send nothing until you activate them."
    >
      <QueryView query={list}>
        {(data) => (
          <div className="grid gap-4 md:grid-cols-2">
            {data.map((t) => (
              <Card key={t.id}>
                <CardContent className="space-y-3 py-5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold">{t.label}</h3>
                      <p className="text-sm text-muted-foreground">{t.description}</p>
                    </div>
                    {t.applied ? <Badge variant="success"><Check aria-hidden /> Added</Badge> : null}
                  </div>
                  <div className="text-sm">
                    <p>
                      <span className="font-medium">Pipeline:</span> {t.pipeline.name} {t.pipeline.present ? <span className="text-muted-foreground">(already here)</span> : null}
                    </p>
                    <p className="text-muted-foreground">{t.pipeline.stages.join(' → ')}</p>
                  </div>
                  {t.sequences.length ? (
                    <ul className="space-y-1 text-sm">
                      {t.sequences.map((s) => (
                        <li key={s.name}>
                          <span className="font-medium">Sequence:</span> {s.name} <span className="text-muted-foreground">· {s.steps} steps ({s.channels.map((c) => CHANNEL_LABELS[c] ?? c).join(', ')}){s.present ? ' · already here' : ''}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <Button
                    size="sm"
                    variant={t.applied ? 'outline' : 'primary'}
                    disabled={t.applied}
                    loading={apply.isPending && apply.variables?.id === t.id}
                    onClick={() =>
                      apply.mutate(
                        { id: t.id },
                        {
                          onSuccess: async (r) => {
                            const added = (r.pipelineId ? 1 : 0) + r.sequenceIds.length;
                            success(added ? `${t.label} template added` : 'Already added', added ? 'Find it under Pipeline and Sequences.' : undefined);
                            await Promise.all([utils.templates.list.invalidate(), utils.crm.pipelines.list.invalidate(), utils.sequences.list.invalidate()]);
                          },
                          onError: (e) => error('Could not add the template', errorMessage(e)),
                        },
                      )
                    }
                  >
                    {t.applied ? 'Added' : 'Add to workspace'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </QueryView>
    </Section>
  );
}

export function TeamPage() {
  const members = trpc.workspace.members.list.useQuery();
  const invites = trpc.workspace.invitations.list.useQuery();
  const assignable = trpc.workspace.roles.assignable.useQuery();
  const invite = trpc.workspace.invitations.create.useMutation();
  const updateMember = trpc.workspace.members.update.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Exclude<Role, 'owner'>>('rep');
  const roleOptions = (assignable.data ?? []).map((r) => ({ value: r, label: ROLE_LABELS[r] }));
  return (
    <div>
      <PageHeader title="Settings" description="Unlimited users on every plan." />
      <SettingsTabs />
      <div className="space-y-6">
        {roleOptions.length ? (
          <Card>
            <CardContent className="flex flex-wrap items-end gap-3 py-5">
              <Field label="Invite by email" className="min-w-64 flex-1"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
              <Field label="Role"><Select value={role} onChange={(e) => setRole(e.target.value as 'rep')} options={roleOptions.filter((o) => o.value !== 'owner')} /></Field>
              <Button disabled={!email} loading={invite.isPending} onClick={() => invite.mutate({ email, role }, { onSuccess: async () => { success('Invitation sent'); setEmail(''); await utils.workspace.invitations.list.invalidate(); }, onError: (e) => error('Could not invite', errorMessage(e)) })}>Send invite</Button>
            </CardContent>
          </Card>
        ) : null}
        <QueryView query={members}>
          {(data) => (
            <Card>
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>{m.email}</TableCell>
                      <TableCell>
                        {roleOptions.length && m.role !== 'owner' ? (
                          <Select className="w-48" value={m.role} aria-label={`Role of ${m.name}`} onChange={(e) => updateMember.mutate({ id: m.id, role: e.target.value as Role }, { onSuccess: () => void utils.workspace.members.list.invalidate(), onError: (er) => error('Could not change role', errorMessage(er)) })} options={roleOptions} />
                        ) : (
                          ROLE_LABELS[m.role]
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </QueryView>
        {invites.data?.length ? (
          <Section title="Pending invitations">
            <Card>
              <CardContent className="divide-y divide-border py-2 text-[14px]">
                {invites.data.map((i) => (
                  <div key={i.id} className="flex justify-between py-3"><span>{i.email}</span><span className="text-muted-foreground">{ROLE_LABELS[i.role]} · expires {formatDate(i.expiresAt)}</span></div>
                ))}
              </CardContent>
            </Card>
          </Section>
        ) : null}
      </div>
    </div>
  );
}

export function BillingPage() {
  const billing = trpc.workspace.billing.get.useQuery();
  const checkout = trpc.workspace.billing.checkout.useMutation();
  const portal = trpc.workspace.billing.portal.useMutation();
  const search = useSearch({ strict: false }) as { checkout?: string };
  const { error } = useToast();
  return (
    <div>
      <PageHeader title="Settings" />
      <SettingsTabs />
      {search.checkout === 'success' ? <Alert variant="success" className="mb-6">Thanks! Your subscription is being confirmed.</Alert> : null}
      <QueryView query={billing}>
        {(b) => (
          <div className="space-y-6">
            <Card>
              <CardContent className="space-y-4 py-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-[20px] font-semibold">{b.planName}</h2>
                    <p className="text-muted-foreground">
                      <Badge variant={b.active ? 'success' : 'destructive'}>{b.state}</Badge>
                      {b.trialDaysLeft !== null ? ` · ${b.trialDaysLeft} trial days left` : ''}
                      {b.currentPeriodEnd ? ` · renews ${formatDate(b.currentPeriodEnd)}` : ''}
                    </p>
                  </div>
                  {b.hive ? (
                    b.hive.billingUrl ? <Button asChild><a href={b.hive.billingUrl}>Manage in Hive</a></Button> : <span className="text-sm">Managed in Hive</span>
                  ) : b.hasSubscription && b.canManage ? (
                    <Button variant="outline" loading={portal.isPending} onClick={() => portal.mutate(undefined, { onSuccess: (r) => { window.location.href = r.url; }, onError: (e) => error('Billing portal unavailable', errorMessage(e)) })}>Manage billing</Button>
                  ) : null}
                </div>
                <Meter label="Voice minutes this month" used={b.usage.voiceMinutes} limit={b.limits.voiceMinutes} unit="min" />
                <Meter label="Prospects this month" used={b.usage.prospects} limit={b.limits.prospectsPerMonth} unit="" />
                <p className="text-[13px] text-muted-foreground">Allowances reset on {formatDate(b.usage.resetsAt)}. Extra minutes are billed at {b.overageCentsPerMinute}¢ a minute where overage is enabled.</p>
              </CardContent>
            </Card>
            {b.hive ? (
              <Alert variant="info">This workspace is billed through WRKRBZ Hive. Change plans and payment details in Hive.</Alert>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {b.plans.map((p) => (
                  <Card key={p.id} className={p.id === b.plan ? 'ring-2 ring-primary' : ''}>
                    <CardContent className="space-y-3 py-6">
                      <div className="flex justify-between"><h3 className="text-[17px] font-semibold">{p.name}</h3>{p.badge ? <Badge variant="primary">{p.badge}</Badge> : null}</div>
                      <p className="font-mono text-[26px] font-semibold">{formatMoney(p.priceCents)}<span className="text-[14px] font-normal text-muted-foreground"> CAD / month</span></p>
                      <ul className="space-y-1 text-[14px]">{p.highlights.map((h) => <li key={h} className="flex gap-2"><Check className="mt-0.5 size-4 text-success" />{h}</li>)}</ul>
                      {b.canManage ? (
                        <Button
                          className="w-full"
                          variant={p.id === b.plan && b.hasSubscription ? 'outline' : 'primary'}
                          disabled={p.id === b.plan && b.hasSubscription}
                          loading={checkout.isPending}
                          onClick={() => checkout.mutate({ plan: p.id }, { onSuccess: (r) => { if (r.url) window.location.href = r.url; }, onError: (e) => error('Checkout unavailable', errorMessage(e)) })}
                        >
                          {p.id === b.plan && b.hasSubscription ? 'Current plan' : `Choose ${p.name}`}
                        </Button>
                      ) : null}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {!b.hive && !b.configured ? <p className="text-sm text-muted-foreground">Stripe is not configured in this environment (STRIPE_SECRET_KEY), so checkout is disabled.</p> : null}
          </div>
        )}
      </QueryView>
    </div>
  );
}

export function IntegrationsPage() {
  return (
    <div>
      <PageHeader title="Settings" />
      <SettingsTabs />
      <div className="space-y-8">
        <FeatureGate feature="crm_sync">
          <CrmSync />
        </FeatureGate>
        <FeatureGate feature="api">
          <ApiKeys />
          <Webhooks />
        </FeatureGate>
      </div>
    </div>
  );
}

function CrmSync() {
  const list = trpc.integrations.crmSync.list.useQuery();
  const connect = trpc.integrations.crmSync.connect.useMutation();
  const sync = trpc.integrations.crmSync.syncNow.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  return (
    <Section title="CRM sync" description="Push SalesBee contacts to HubSpot or Salesforce.">
      <div className="grid gap-4 md:grid-cols-2">
        {(['hubspot', 'salesforce'] as const).map((p) => {
          const conn = list.data?.find((c) => c.provider === p);
          return (
            <Card key={p}>
              <CardContent className="space-y-3 py-5">
                <h3 className="text-[16px] font-semibold capitalize">{p}</h3>
                <p className="text-sm text-muted-foreground">{conn ? `Connected${conn.live ? '' : ' (stub adapter: nothing leaves SalesBee)'}${conn.lastSyncAt ? ` · last sync ${formatDate(conn.lastSyncAt)}` : ''}` : 'Not connected'}</p>
                {conn ? (
                  <Button size="sm" loading={sync.isPending} onClick={() => sync.mutate({ provider: p }, { onSuccess: async (r) => { success(`Pushed ${r.pushed} contacts`); await utils.integrations.crmSync.list.invalidate(); }, onError: (e) => error('Sync failed', errorMessage(e)) })}>Sync now</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => connect.mutate({ provider: p }, { onSuccess: () => void utils.integrations.crmSync.list.invalidate() })}>Connect</Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </Section>
  );
}

function ApiKeys() {
  const keys = trpc.integrations.apiKeys.list.useQuery();
  const create = trpc.integrations.apiKeys.create.useMutation();
  const revoke = trpc.integrations.apiKeys.revoke.useMutation();
  const utils = trpc.useUtils();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'read' | 'write' | 'admin'>('read');
  const [shown, setShown] = useState<string | null>(null);
  return (
    <Section title="API keys" description="Bearer keys for the REST API at /v1 (e.g. GET /v1/crm/contacts).">
      {shown ? <Alert variant="success" title="Copy your key now: it is shown once">{shown}</Alert> : null}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-5">
          <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Scope"><Select value={scope} onChange={(e) => setScope(e.target.value as 'read')} options={[{ value: 'read', label: 'Read' }, { value: 'write', label: 'Write' }, { value: 'admin', label: 'Admin' }]} /></Field>
          <Button disabled={!name} loading={create.isPending} onClick={() => create.mutate({ name, scopes: [scope] }, { onSuccess: async (r) => { setShown(r.key); setName(''); await utils.integrations.apiKeys.list.invalidate(); } })}>Create key</Button>
        </CardContent>
      </Card>
      <QueryView query={keys}>
        {(data) => (
          <Card>
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Key</TableHead><TableHead>Scopes</TableHead><TableHead>Last used</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {data.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell>{k.name}</TableCell>
                    <TableCell className="font-mono text-[13px]">{k.prefix}…</TableCell>
                    <TableCell>{k.scopes.join(', ')}</TableCell>
                    <TableCell>{k.lastUsedAt ? formatDate(k.lastUsedAt) : '—'}</TableCell>
                    <TableCell className="text-right">{k.revokedAt ? <Badge>revoked</Badge> : <Button size="sm" variant="ghost" onClick={() => revoke.mutate({ id: k.id }, { onSuccess: () => void utils.integrations.apiKeys.list.invalidate() })}>Revoke</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </QueryView>
    </Section>
  );
}

function Webhooks() {
  const hooks = trpc.integrations.webhooks.list.useQuery();
  const create = trpc.integrations.webhooks.create.useMutation();
  const utils = trpc.useUtils();
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<Set<WebhookEvent>>(new Set(['call.completed']));
  const [secret, setSecret] = useState<string | null>(null);
  return (
    <Section title="Webhooks" description="Signed with X-SalesBee-Signature (t=…,v1=HMAC-SHA256).">
      {secret ? <Alert variant="success" title="Signing secret (shown once)">{secret}</Alert> : null}
      <Card>
        <CardContent className="space-y-3 py-5">
          <Field label="Endpoint URL"><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/hooks/salesbee" /></Field>
          <div className="flex flex-wrap gap-4">
            {WEBHOOK_EVENTS.map((e) => (
              <Checkbox key={e} label={e} checked={events.has(e)} onCheckedChange={(v) => setEvents((s) => { const n = new Set(s); if (v) n.add(e); else n.delete(e); return n; })} />
            ))}
          </div>
          <Button disabled={!url || events.size === 0} loading={create.isPending} onClick={() => create.mutate({ url, events: [...events] }, { onSuccess: async (r) => { setSecret(r.secret); setUrl(''); await utils.integrations.webhooks.list.invalidate(); } })}>Add endpoint</Button>
          {create.error ? <p className="text-sm text-destructive">{errorMessage(create.error)}</p> : null}
        </CardContent>
      </Card>
      <QueryView query={hooks}>
        {(data) => (
          <Card>
            <CardContent className="divide-y divide-border py-2 text-[14px]">
              {data.endpoints.length === 0 ? <p className="py-3 text-muted-foreground">No endpoints.</p> : null}
              {data.endpoints.map((e) => (
                <div key={e.id} className="flex flex-wrap justify-between gap-2 py-3"><span className="font-mono text-[13px]">{e.url}</span><span className="text-muted-foreground">{e.events.join(', ')}</span></div>
              ))}
            </CardContent>
          </Card>
        )}
      </QueryView>
    </Section>
  );
}
